
import { useAuth } from "@/app/providers/AuthContext";
import { supabase } from "@/shared/api/supabaseClient";
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
  useMemo,
} from "react";

export interface FormatSettings {
  dateFormat: string;
  timeFormat: string;
  dayOfWeekFormat: string;
  monthFormat: string;
  monthAndYearFormat: string;
  currency: "CHF" | "EUR";
  timezone: string;
}

export interface WeatherSettings {
  enabled: boolean;
  mode: "off" | "static" | "animated";
  updateInterval: number; // minutes
}

export interface InvoiceSettings {
  includeDetail: boolean;
}

interface SettingsContextType {
  formats: FormatSettings;
  weatherSettings: WeatherSettings;
  invoiceSettings: InvoiceSettings;
  setFormats: (formats: Partial<FormatSettings>) => Promise<void>;
  setWeatherSettings: (settings: Partial<WeatherSettings>) => void;
  loading: boolean;
  error: string | null;
}

const SettingsContext = createContext<SettingsContextType | undefined>(
  undefined
);

interface SettingsProviderProperties {
  children: ReactNode;
}

const defaultFormats: FormatSettings = {
  dateFormat: "dd.MM.yyyy",
  timeFormat: "HH:mm",
  dayOfWeekFormat: "EEE",
  monthFormat: "MMMM",
  monthAndYearFormat: "MMMM yyyy",
  currency: "CHF",
  timezone: "Europe/Zurich",
};

const defaultWeatherSettings: WeatherSettings = {
  enabled: true,
  mode: "animated",
  updateInterval: 20,
};

const defaultInvoiceSettings: InvoiceSettings = {
  includeDetail: false,
};

export const SettingsProvider: React.FC<SettingsProviderProperties> = ({
  children,
}) => {
  const { activePracticeId, isReady } = useAuth();
  const [formats, setFormatsState] = useState<FormatSettings>(defaultFormats);
  const [weatherSettings, setWeatherSettingsState] = useState<WeatherSettings>(defaultWeatherSettings);
  const [invoiceSettings, setInvoiceSettingsState] = useState<InvoiceSettings>(defaultInvoiceSettings);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isReady) return;

    if (activePracticeId) {
      setLoading(true);
      // The Supabase builder is a thenable (PromiseLike), not a full Promise, so it has
      // no .catch(). Await it inside an IIFE with try/catch so an actual rejection (not the
      // resolved { error }) can't float to window.onunhandledrejection with a raw
      // { code, message } object (see Sentry ZERMMI-44).
      void (async () => {
        try {
          const { data, error: databaseError } = await supabase
            .from("practices")
            .select("currency, date_format, tzid, invoice_include_detail")
            .eq("id", activePracticeId)
            .single();

          if (databaseError) {
            console.error("Error fetching practice settings:", databaseError);
            setError("Failed to load practice settings.");
          } else if (data) {
            setFormatsState((previous) => ({
              ...previous,
              currency: data.currency || "CHF",
              dateFormat: data.date_format || "dd.MM.yyyy",
              timezone: data.tzid || "Europe/Zurich",
            }));
            setInvoiceSettingsState({
              includeDetail: data.invoice_include_detail ?? false,
            });
          }
        } catch (databaseError) {
          console.error("Error fetching practice settings:", databaseError);
          setError("Failed to load practice settings.");
        } finally {
          setLoading(false);
        }
      })();
    } else {
      setFormatsState(defaultFormats);
      setWeatherSettingsState(defaultWeatherSettings);
      setInvoiceSettingsState(defaultInvoiceSettings);
      setLoading(false);
    }
  }, [activePracticeId, isReady]);

  const setFormats = useCallback(
    async (newFormats: Partial<FormatSettings>) => {
      if (!activePracticeId) {
        setError("Cannot save settings: No practice selected.");
        return;
      }

      const updates: Record<string, any> = {};
      if (newFormats.currency) updates.currency = newFormats.currency;
      if (newFormats.dateFormat) updates.date_format = newFormats.dateFormat;

      if (Object.keys(updates).length === 0) return;

      setLoading(true);
      const { error: databaseError } = await supabase
        .from("practices")
        .update(updates)
        .eq("id", activePracticeId);

      if (databaseError) {
        console.error("Error updating practice settings:", databaseError);
        setError("Failed to save settings.");
      } else {
        setFormatsState((previous) => ({ ...previous, ...newFormats }));
        setError(null);
      }
      setLoading(false);
    },
    [activePracticeId]
  );

  const setWeatherSettings = useCallback(
    (newSettings: Partial<WeatherSettings>) => {
      setWeatherSettingsState((previous) => ({ ...previous, ...newSettings }));
    },
    []
  );

  const value = useMemo(
    () => ({
      formats,
      weatherSettings,
      invoiceSettings,
      setFormats,
      setWeatherSettings,
      loading,
      error,
    }),
    [formats, weatherSettings, invoiceSettings, setFormats, setWeatherSettings, loading, error]
  );

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useSettings = (): SettingsContextType => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    // Return default settings when no provider is available (e.g., on public routes)
    return {
      formats: defaultFormats,
      weatherSettings: defaultWeatherSettings,
      invoiceSettings: defaultInvoiceSettings,
      setFormats: async () => {
        // No-op when no provider is available
      },
      setWeatherSettings: () => {
        // No-op when no provider is available
      },
      loading: false,
      error: null,
    };
  }
  return context;
};
